// Audio service using Web Speech API

class AudioService {
  private synth: SpeechSynthesis | null = null;
  private voice: SpeechSynthesisVoice | null = null;
  private isSupported: boolean = false;

  constructor() {
    // Check if speech synthesis is available
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      this.synth = window.speechSynthesis;
      this.isSupported = true;
      this.loadVoice();
    } else {
      console.warn('Speech synthesis not supported in this browser');
    }
  }

  private loadVoice() {
    if (!this.synth) return;

    const loadVoices = () => {
      const voices = this.synth?.getVoices() || [];
      // Prefer English voices
      this.voice = voices.find(v => v.lang.startsWith('en-')) || voices[0] || null;
    };

    loadVoices();
    // Chrome loads voices asynchronously
    if (typeof speechSynthesis !== 'undefined' && speechSynthesis.onvoiceschanged !== undefined) {
      speechSynthesis.onvoiceschanged = loadVoices;
    }
  }

  speak(text: string, rate: number = 0.9): Promise<void> {
    return new Promise((resolve) => {
      // If not supported, just resolve silently
      if (!this.isSupported || !this.synth) {
        console.warn('Speech synthesis not available');
        resolve();
        return;
      }

      try {
        // Cancel any ongoing speech
        this.synth.cancel();

        const utterance = new SpeechSynthesisUtterance(text);
        if (this.voice) {
          utterance.voice = this.voice;
        }
        utterance.rate = rate;
        utterance.pitch = 1;
        utterance.volume = 1;
        utterance.lang = 'en-US';

        utterance.onend = () => resolve();
        utterance.onerror = (e) => {
          // Log but don't throw - audio errors shouldn't break the app
          console.warn('Speech synthesis error:', e.error || 'unknown error');
          resolve();
        };

        this.synth.speak(utterance);
      } catch (error) {
        console.warn('Speech synthesis failed:', error);
        resolve();
      }
    });
  }

  stop() {
    if (this.synth) {
      this.synth.cancel();
    }
  }
}

export const audioService = new AudioService();
export const speak = (text: string, rate?: number) => audioService.speak(text, rate);
export const stopSpeaking = () => audioService.stop();

